/**
 * Dependency Matrix Panel Component
 *
 * Renders a dependency matrix/graph for a specification showing
 * source-to-target code relationships discovered by scans, manual entry, or AI.
 *
 * Features:
 * - Matrix table with source elements on Y-axis and target elements on X-axis
 * - Color-coded relationship types
 * - Strength badges (direct/indirect/suggested)
 * - Confidence score as opacity
 * - Summary statistics
 *
 * Schema: code_dependencies table (migration 022)
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 *
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Table, Spinner, Alert } from 'flowbite-react';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ElementType = 'spec' | 'file' | 'function' | 'class';
export type RelationshipType =
  | 'imports'
  | 'calls'
  | 'implements'
  | 'extends'
  | 'tests'
  | 'documents'
  | 'related_to'
  | 'blocks'
  | 'depends_on';
export type Strength = 'direct' | 'indirect' | 'suggested';
export type DiscoveredBy = 'scan' | 'manual' | 'ai';

export interface CodeDependency {
  Dependency_ID: string;
  Source_Type: ElementType;
  Source_ID: string;
  Source_Name: string | null;
  Target_Type: ElementType;
  Target_ID: string;
  Target_Name: string | null;
  Relationship_Type: RelationshipType;
  Strength: Strength;
  Confidence_Score: number;
  Discovered_By: DiscoveredBy;
  Discovered_At: string;
}

interface DependencyMatrixPanelProps {
  specId: string;
  clientId: string;
}

// ---------------------------------------------------------------------------
// Color maps
// ---------------------------------------------------------------------------

const RELATIONSHIP_COLORS: Record<RelationshipType, string> = {
  imports: 'bg-blue-100 text-blue-800',
  calls: 'bg-green-100 text-green-800',
  implements: 'bg-purple-100 text-purple-800',
  extends: 'bg-indigo-100 text-indigo-800',
  tests: 'bg-yellow-100 text-yellow-800',
  documents: 'bg-gray-100 text-gray-800',
  related_to: 'bg-cyan-100 text-cyan-800',
  blocks: 'bg-red-100 text-red-800',
  depends_on: 'bg-orange-100 text-orange-800',
};

const RELATIONSHIP_BADGE_COLORS: Record<RelationshipType, 'info' | 'success' | 'purple' | 'indigo' | 'warning' | 'gray' | 'failure' | 'dark'> = {
  imports: 'info',
  calls: 'success',
  implements: 'purple',
  extends: 'indigo',
  tests: 'warning',
  documents: 'gray',
  related_to: 'dark',
  blocks: 'failure',
  depends_on: 'warning',
};

const STRENGTH_BORDER: Record<Strength, string> = {
  direct: 'border-2 border-solid',
  indirect: 'border-2 border-dashed',
  suggested: 'border-2 border-dotted',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DependencyMatrixPanel: React.FC<DependencyMatrixPanelProps> = ({
  specId,
  clientId,
}) => {
  const [dependencies, setDependencies] = useState<CodeDependency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDependencies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams: Record<string, string> = {
        Client_ID: clientId,
        spec_id: specId,
      };

      const response = await Make_Authorized_API_Call<CodeDependency>(
        API_ENDPOINTS.SPECIFICATIONS.DEPENDENCIES_GET,
        'GET',
        undefined,
        { params: queryParams }
      );

      if (isSuccessResponse(response)) {
        setDependencies(response.data?.Records || []);
      } else {
        throw new Error(response.message || 'Failed to load dependencies');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load dependencies';
      console.error('[DependencyMatrixPanel] Error:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [specId, clientId]);

  useEffect(() => {
    if (specId && clientId) {
      fetchDependencies();
    }
  }, [specId, clientId, fetchDependencies]);

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  const directCount = dependencies.filter((d) => d.Strength === 'direct').length;
  const indirectCount = dependencies.filter((d) => d.Strength === 'indirect').length;
  const suggestedCount = dependencies.filter((d) => d.Strength === 'suggested').length;

  // Build unique source and target elements for the matrix axes
  const sourceElements = Array.from(
    new Map(
      dependencies.map((d) => [
        `${d.Source_Type}:${d.Source_ID}`,
        { type: d.Source_Type, id: d.Source_ID, name: d.Source_Name },
      ])
    ).values()
  );

  const targetElements = Array.from(
    new Map(
      dependencies.map((d) => [
        `${d.Target_Type}:${d.Target_ID}`,
        { type: d.Target_Type, id: d.Target_ID, name: d.Target_Name },
      ])
    ).values()
  );

  // Build lookup map for matrix cells: "sourceType:sourceId -> targetType:targetId" => dependency
  const depMap = new Map<string, CodeDependency>();
  dependencies.forEach((d) => {
    const key = `${d.Source_Type}:${d.Source_ID}|${d.Target_Type}:${d.Target_ID}`;
    depMap.set(key, d);
  });

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const renderElementLabel = (el: { type: string; id: string; name: string | null }) => {
    const label = el.name || el.id;
    const truncated = label.length > 30 ? label.slice(0, 27) + '...' : label;
    return (
      <span className="flex items-center gap-1" title={label}>
        <Badge color="gray" size="xs">
          {el.type}
        </Badge>
        <span className="text-xs font-mono truncate">{truncated}</span>
      </span>
    );
  };

  const renderCell = (
    source: { type: string; id: string },
    target: { type: string; id: string }
  ) => {
    const key = `${source.type}:${source.id}|${target.type}:${target.id}`;
    const dep = depMap.get(key);

    if (!dep) {
      return <span className="text-gray-300">-</span>;
    }

    const opacity = Math.max(0.3, dep.Confidence_Score);
    const colorClass = RELATIONSHIP_COLORS[dep.Relationship_Type] || 'bg-gray-100 text-gray-800';
    const borderClass = STRENGTH_BORDER[dep.Strength] || '';

    return (
      <span
        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${colorClass} ${borderClass}`}
        style={{ opacity }}
        title={`${dep.Relationship_Type} (${dep.Strength}, confidence: ${dep.Confidence_Score})`}
      >
        {dep.Relationship_Type}
      </span>
    );
  };

  // -------------------------------------------------------------------------
  // Loading / Error / Empty states
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="failure">
        <p className="font-semibold">Error loading dependencies</p>
        <p className="text-sm mt-1">{error}</p>
      </Alert>
    );
  }

  if (dependencies.length === 0) {
    return (
      <Card>
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg mb-2">No dependencies found.</p>
          <p className="text-gray-400 text-sm">
            Link a repository and run a scan to discover dependencies.
          </p>
        </div>
      </Card>
    );
  }

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{dependencies.length}</div>
            <div className="text-xs text-gray-500">Total Dependencies</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{directCount}</div>
            <div className="text-xs text-gray-500">Direct</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{indirectCount}</div>
            <div className="text-xs text-gray-500">Indirect</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-500">{suggestedCount}</div>
            <div className="text-xs text-gray-500">Suggested</div>
          </div>
        </Card>
      </div>

      {/* Legend */}
      <Card>
        <div className="flex flex-wrap gap-3 items-center">
          <span className="text-xs font-medium text-gray-500 mr-2">Relationship types:</span>
          {(Object.keys(RELATIONSHIP_BADGE_COLORS) as RelationshipType[]).map((rel) => (
            <Badge key={rel} color={RELATIONSHIP_BADGE_COLORS[rel]} size="xs">
              {rel}
            </Badge>
          ))}
        </div>
        <div className="flex flex-wrap gap-4 items-center mt-2">
          <span className="text-xs font-medium text-gray-500 mr-2">Strength:</span>
          <span className="text-xs border-2 border-solid border-gray-400 px-2 py-0.5 rounded">
            solid = direct
          </span>
          <span className="text-xs border-2 border-dashed border-gray-400 px-2 py-0.5 rounded">
            dashed = indirect
          </span>
          <span className="text-xs border-2 border-dotted border-gray-400 px-2 py-0.5 rounded">
            dotted = suggested
          </span>
          <span className="text-xs text-gray-500 ml-2">
            Opacity = confidence score
          </span>
        </div>
      </Card>

      {/* Matrix Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table striped>
            <Table.Head>
              <Table.HeadCell className="min-w-[180px]">
                Source / Target
              </Table.HeadCell>
              {targetElements.map((target) => (
                <Table.HeadCell key={`${target.type}:${target.id}`} className="min-w-[120px]">
                  {renderElementLabel(target)}
                </Table.HeadCell>
              ))}
            </Table.Head>
            <Table.Body className="divide-y">
              {sourceElements.map((source) => (
                <Table.Row key={`${source.type}:${source.id}`}>
                  <Table.Cell className="font-medium">
                    {renderElementLabel(source)}
                  </Table.Cell>
                  {targetElements.map((target) => (
                    <Table.Cell
                      key={`${source.type}:${source.id}|${target.type}:${target.id}`}
                      className="text-center"
                    >
                      {renderCell(source, target)}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </Card>

      {/* Flat list for accessibility / detail */}
      <Card>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">All Dependencies</h3>
        <Table>
          <Table.Head>
            <Table.HeadCell>Source</Table.HeadCell>
            <Table.HeadCell>Relationship</Table.HeadCell>
            <Table.HeadCell>Target</Table.HeadCell>
            <Table.HeadCell>Strength</Table.HeadCell>
            <Table.HeadCell>Confidence</Table.HeadCell>
            <Table.HeadCell>Discovered</Table.HeadCell>
          </Table.Head>
          <Table.Body className="divide-y">
            {dependencies.map((dep) => (
              <Table.Row key={dep.Dependency_ID}>
                <Table.Cell>
                  <span className="flex items-center gap-1">
                    <Badge color="gray" size="xs">{dep.Source_Type}</Badge>
                    <span className="text-xs font-mono">{dep.Source_Name || dep.Source_ID}</span>
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <Badge color={RELATIONSHIP_BADGE_COLORS[dep.Relationship_Type] || 'gray'}>
                    {dep.Relationship_Type}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <span className="flex items-center gap-1">
                    <Badge color="gray" size="xs">{dep.Target_Type}</Badge>
                    <span className="text-xs font-mono">{dep.Target_Name || dep.Target_ID}</span>
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <Badge color={dep.Strength === 'direct' ? 'info' : dep.Strength === 'indirect' ? 'warning' : 'gray'}>
                    {dep.Strength}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm">{(dep.Confidence_Score * 100).toFixed(0)}%</span>
                </Table.Cell>
                <Table.Cell>
                  <Badge color="gray" size="xs">{dep.Discovered_By}</Badge>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Card>
    </div>
  );
};

export default DependencyMatrixPanel;
